import { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { StatusPill } from '@/components/ui/StatusPill';
import { TypedConfirmDialog } from '@/components/ui/TypedConfirmDialog';
import { errorMessage, tokenApi, type TokenSession } from './api';
import { formatDateTime, providerLabel, timeAgo, timeUntil } from './format';
import {
  EmptyRow,
  Pager,
  ProviderCell,
  TableShell,
  Td,
  TenantCell,
  TenantSelect,
  Th,
} from './shared';
import { controlClass, useDebounced, useTenantOptions } from './hooks';

const PAGE_SIZE = 25;

const STATUS_PILL: Record<TokenSession['status'], string> = {
  active: 'active',
  expired: 'inactive',
  revoked: 'error',
};

export const SessionsTab = () => {
  const tenants = useTenantOptions();
  const [tenant, setTenant] = useState('');
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: TokenSession[]; total: number }>({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [toRevoke, setToRevoke] = useState<TokenSession | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tokenApi.sessions({
        tenant,
        status,
        user: debouncedSearch,
        page,
        page_size: PAGE_SIZE,
      });
      setData({ items: res.items, total: res.total });
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to load sessions'));
    } finally {
      setLoading(false);
    }
  }, [tenant, status, debouncedSearch, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [tenant, status, debouncedSearch]);

  const revoke = async () => {
    if (!toRevoke) return;
    setRevoking(true);
    try {
      await tokenApi.revokeSession(toRevoke.id);
      toast.success(`Session for ${toRevoke.email} revoked`);
      setToRevoke(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to revoke session'));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by user email"
            aria-label="Search by user email"
            className={`${controlClass} pl-9 w-64`}
          />
        </div>
        <TenantSelect value={tenant} onChange={setTenant} tenants={tenants} />
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} className={controlClass}>
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      <TableShell footer={<Pager page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} />}>
        <thead className="border-b border-slate-100 dark:border-slate-800">
          <tr>
            <Th>User</Th>
            <Th>Tenant</Th>
            <Th>Provider</Th>
            <Th>Status</Th>
            <Th>Signed in</Th>
            <Th>Last activity</Th>
            <Th>Ends</Th>
            <Th />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.items.length === 0 ? (
            <EmptyRow columns={8} loading={loading} />
          ) : (
            data.items.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30">
                <Td className="font-semibold">{s.email}</Td>
                <Td>
                  <TenantCell slug={s.tenant} name={s.tenant_name} tenants={tenants} />
                </Td>
                <Td>
                  <ProviderCell provider={s.provider} />
                  {s.status === 'active' && !s.idp_refresh_supported && (
                    <span className="block text-[11px] text-slate-400">identity token not renewable</span>
                  )}
                </Td>
                <Td>
                  <StatusPill status={STATUS_PILL[s.status]} label={s.status} />
                </Td>
                <Td className="whitespace-nowrap" >
                  <span title={formatDateTime(s.auth_time)}>{timeAgo(s.auth_time)}</span>
                </Td>
                <Td className="whitespace-nowrap">
                  <span title={formatDateTime(s.last_activity_at)}>{timeAgo(s.last_activity_at)}</span>
                </Td>
                <Td className="whitespace-nowrap">
                  <span title={formatDateTime(s.expires_at)}>
                    {s.status === 'active' ? timeUntil(s.expires_at) : '—'}
                  </span>
                </Td>
                <Td className="text-right">
                  {s.status === 'active' && (
                    <Button variant="ghost" size="sm" onClick={() => setToRevoke(s)}>
                      Revoke
                    </Button>
                  )}
                </Td>
              </tr>
            ))
          )}
        </tbody>
      </TableShell>

      {toRevoke && (
        <TypedConfirmDialog
          title="Revoke session"
          confirmText={toRevoke.email}
          confirmLabel="Revoke session"
          isLoading={revoking}
          onClose={() => setToRevoke(null)}
          onConfirm={revoke}
          description={
            <>
              This ends only the session <span className="font-bold text-slate-900 dark:text-white">{toRevoke.email}</span>{' '}
              started on {formatDateTime(toRevoke.auth_time)} in {toRevoke.tenant_name ?? toRevoke.tenant} via{' '}
              {providerLabel(toRevoke.provider)}. Refresh tokens issued from this session stop working, so applications
              can no longer renew them. Access tokens already issued keep working until they expire (up to the access
              token lifetime). Other sessions of this user, such as on another browser or device, are not affected, and
              the user can sign in again.
            </>
          }
        />
      )}
    </div>
  );
};
